import { Link, Navigate, useParams } from 'react-router-dom';

import { Button, Card, CardContent } from '@/components/cargofl-ui';
import { FullPageSpinner } from '@/components/shell/full-page-spinner';
import { PageContainer } from '@/components/shell/page-container';
import { useAssetByCode } from '@/lib/assets-api';
import { useMyAssets } from '@/lib/operations-api';
import { useCan } from '@/lib/session';

/**
 * Where a scanned QR sticker lands: /a/AST-1007 → the asset's detail screen.
 *
 * Deliberately the shortest path this app owns. It is typed by hand as often as it is
 * scanned - somebody reading a sticker across a desk - so /a/ rather than
 * /assets/by-code/, and the code is matched case-insensitively by the API for the same
 * reason.
 *
 * `replace` on the redirect: the scan is not a page anyone wants to come back to, and
 * without it Back from the asset lands here and bounces straight forward again.
 *
 * Two audiences, and they must not be given the same answer.
 *
 * Somebody with `viewRegister` gets the register's detail screen, which is the point of
 * putting a code on a sticker. A plain employee scanning the laptop in their own hands
 * is asking a much smaller question - "is this the one issued to me, and how do I report
 * it" - and the register would answer it by also showing them price, purchase history
 * and everyone who held it before. So they are sent to their own assets with that row
 * picked out instead. It needs no permission and no new endpoint: the code is already on
 * the assignment rows they can read.
 *
 * The page used to sit behind `viewRegister` outright, which bounced them to /my-assets
 * with no explanation - indistinguishable from the sticker having done nothing at all.
 */
export function AssetByCodePage() {
  const { code } = useParams<{ code: string }>();
  const canSeeRegister = useCan('viewRegister');

  // Only asked when it can be answered - to an employee this route 403s, and a failed
  // read is now a toast rather than nothing.
  const { data, isPending, isError } = useAssetByCode(canSeeRegister ? (code ?? null) : null);
  const { data: mine, isPending: minePending } = useMyAssets();

  if (canSeeRegister) {
    if (isPending) return <FullPageSpinner />;
    if (!isError && data) return <Navigate to={`/assets/${data.id}`} replace />;
    return <UnknownCode code={code} canSeeRegister />;
  }

  if (minePending) return <FullPageSpinner />;

  // Newest first, so a code that has been issued, returned and issued again picks out
  // the assignment that is actually open.
  const held = mine?.find(
    (assignment) => assignment.asset.code.toLowerCase() === (code ?? '').toLowerCase(),
  );
  if (held) return <Navigate to={`/my-assets?asset=${encodeURIComponent(held.id)}`} replace />;

  return <UnknownCode code={code} canSeeRegister={false} />;
}

function UnknownCode({ code, canSeeRegister }: { code?: string; canSeeRegister: boolean }) {
  return (
    <PageContainer>
      <Card>
        <CardContent className="py-12 text-center sm:py-12">
          <p className="at-num text-sm font-medium">{code}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {canSeeRegister
              ? 'No asset carries that code. Either the sticker is from an older system, or the asset was deleted before it was ever issued.'
              : 'That asset is not one of yours. Only the things issued to you are yours to look at here — ask IT if you need to know what it is.'}
          </p>
          <Button asChild variant="secondary" size="sm" className="mt-4">
            <Link to={canSeeRegister ? '/assets' : '/my-assets'}>
              {canSeeRegister ? 'Search the register' : 'Back to my assets'}
            </Link>
          </Button>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
